import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { permissionAdminService, type ServiceContext } from '@/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Schema                                                             */
/* ------------------------------------------------------------------ */

const permissionFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  code: z
    .string()
    .min(1, 'Code is required')
    .max(100, 'Code must be 100 characters or less')
    .regex(/^[A-Z0-9_]+$/, 'Only uppercase letters, numbers, and underscores'),
  description: z.string().max(500).optional().or(z.literal('')),
  category: z.string().max(100).optional().or(z.literal('')),
});

type PermissionFormValues = z.infer<typeof permissionFormSchema>;

const defaultValues: PermissionFormValues = {
  name: '',
  code: '',
  description: '',
  category: '',
};

/* ------------------------------------------------------------------ */
/*  Code auto-generation from name                                     */
/* ------------------------------------------------------------------ */

function toCode(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 100);
}

/* ------------------------------------------------------------------ */
/*  Save action type                                                   */
/* ------------------------------------------------------------------ */

type SaveAction = 'close' | 'new' | 'open';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface NewPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ServiceContext;
  onCreated: () => void;
}

export default function NewPermissionDialog({
  open,
  onOpenChange,
  context,
  onCreated,
}: NewPermissionDialogProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const userEditedCode = useRef(false);

  const form = useForm<PermissionFormValues>({
    resolver: zodResolver(permissionFormSchema),
    defaultValues,
  });

  // Reset form and tracking when dialog opens
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
      userEditedCode.current = false;
    }
  }, [open, form]);

  // Auto-generate code from name (unless user manually edited code)
  const nameValue = form.watch('name');
  useEffect(() => {
    if (!userEditedCode.current) {
      form.setValue('code', toCode(nameValue));
    }
  }, [nameValue, form]);

  const handleSave = async (action: SaveAction) => {
    const valid = await form.trigger();
    if (!valid) return;

    const values = form.getValues();
    setIsSaving(true);

    try {
      const result = await permissionAdminService.create(
        {
          name: values.name,
          code: values.code,
          description: values.description || null,
          category: values.category || null,
        },
        context
      );

      if (result.error) {
        toast.error(result.error.message);
        return;
      }

      toast.success(`Permission "${values.name}" created`);
      onCreated();

      switch (action) {
        case 'close':
          onOpenChange(false);
          break;
        case 'new':
          form.reset(defaultValues);
          userEditedCode.current = false;
          break;
        case 'open':
          onOpenChange(false);
          navigate(`/security/permissions/${result.data!.id}`);
          break;
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Permission</DialogTitle>
          <DialogDescription>
            Create a new permission that can be assigned to users and groups.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave('close');
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Manage Invoices" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. MANAGE_INVOICES"
                      className="font-mono"
                      {...field}
                      onChange={(e) => {
                        userEditedCode.current = true;
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Invoicing" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional description of what this permission grants..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={() => handleSave('new')}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save &amp; New
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave('open')}
              disabled={isSaving}
            >
              Save &amp; Open
            </Button>
            <Button
              onClick={() => handleSave('close')}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save &amp; Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
